import {
    DataConnection,
    DataGlueGraph,
    FunctionDefinition,
    GlueVariable,
    isFunctionNodeInstance,
    NodeInstance,
    NodeInstanceDataPort,
} from '@glueapp/graph-types';
import { Node } from '@glueapp/graph-types';
import {
    DataType,
    FunctionDataType,
    typeAMatchesTypeB,
    unwrapAllResolvedGenerics,
    getStringRepresentationFromType,
    createOptional,
    isOptionalType,
} from '@glueapp/graph-types';
import { dictionaryFromNumberTuple, dictionaryFromStringTuple } from '@glueapp/graph-types';
import { isFunctionReferenceValue } from '@glueapp/graph-types';

type Generic = { identifier: string; type: DataType | undefined };

// Debug logging for type resolution - set to true to enable
const DEBUG_TYPE_RESOLUTION = process.env.DEBUG_TYPE_RESOLUTION === 'true';

function debugLog(message: string, data?: any) {
    if (DEBUG_TYPE_RESOLUTION) {
        console.log(`[GraphTypeResolver] ${message}`, data ? JSON.stringify(data, null, 2) : '');
    }
}

function resolveGenericsInType(dataType: DataType, genericIdentifierTypePairs: { [key: string]: Generic }): DataType {
    switch (dataType.kind) {
        case 'string':
        case 'number':
        case 'boolean':
        case 'stringLiteral':
        case 'numberLiteral':
        case 'null':
        case 'secret':
            return dataType;
        case 'object':
            return {
                kind: 'object',
                name: dataType.name,
                restriction: dataType.restriction,
                properties: dictionaryFromStringTuple(Object.entries(dataType.properties), ([propertyName, propertyType]) => [
                    propertyName,
                    resolveGenericsInType(propertyType, genericIdentifierTypePairs),
                ]),
                optionalProperties: dataType.optionalProperties,
            };
        case 'array':
            return {
                kind: 'array',
                name: dataType.name,
                subType: resolveGenericsInType(dataType.subType, genericIdentifierTypePairs),
            };
        case 'union':
            return {
                kind: 'union',
                name: dataType.name,
                subTypes: dataType.subTypes.map(subType => resolveGenericsInType(subType, genericIdentifierTypePairs)) as [
                    DataType,
                    ...DataType[],
                ],
            };
        case 'generic':
            const resolvedGeneric: Generic | undefined = genericIdentifierTypePairs[dataType.identifier];
            // If generic is there and has a type value
            if (resolvedGeneric !== undefined && resolvedGeneric.type !== undefined) {
                return {
                    ...dataType,
                    name: dataType.name,
                    resolvedType: resolvedGeneric.type,
                };
            } else {
                return dataType;
            }
        case 'shape':
            return {
                kind: 'shape',
                name: dataType.name,
                restriction: dataType.restriction ? resolveGenericsInType(dataType.restriction, genericIdentifierTypePairs) : undefined,
            };
        case 'optional':
            return {
                kind: 'optional',
                name: dataType.name,
                innerType: resolveGenericsInType(dataType.innerType, genericIdentifierTypePairs),
            };
        case 'function':
            return {
                kind: 'function',
                name: dataType.name,
                inputTypes: Object.fromEntries(
                    Object.entries(dataType.inputTypes).map(([portName, portType]) => [
                        portName,
                        resolveGenericsInType(portType, genericIdentifierTypePairs),
                    ]),
                ),
                outputTypes: Object.fromEntries(
                    Object.entries(dataType.outputTypes).map(([portName, portType]) => [
                        portName,
                        resolveGenericsInType(portType, genericIdentifierTypePairs),
                    ]),
                ),
            };
        default:
            throw new Error(`Unknown DataType kind: ${(dataType as any).kind}`);
    }
}

function arrayHasAtLeastOneElement<T>(array: T[]): array is [T, ...T[]] {
    return array.length > 0;
}

export class GraphTypeResolver {
    private nodeInstances: NodeInstance[];
    private connections: DataConnection[];
    private readonly variables: { [key: string]: GlueVariable };
    private readonly functions: FunctionDefinition[];
    private readonly nodeLibraryIDDictionary: { [key: string]: Node };

    // Function-specific resolved node instances to handle ID collisions
    private resolvedNodeInstances: { [key: number]: NodeInstance } = {};
    private functionResolvedNodeInstances: { [functionId: string]: { [nodeId: number]: NodeInstance } } = {};
    // Store the resolved function's internal graph nodes (not the function definition itself)
    private resolvedFunctionGraphs: { [functionId: string]: { nodeInstances: NodeInstance[] } } = {};

    // Track current function context
    private currentFunctionContext: string | null = null;

    constructor(graph: DataGlueGraph, nodeLibraryIDDictionary: { [key: string]: Node }) {
        const { nodeInstances, connections, variables, functions } = graph;
        this.nodeInstances = nodeInstances;
        this.connections = connections;
        this.functions = functions || []; // Preserve original functions
        this.variables = dictionaryFromStringTuple(variables, variable => {
            if (variable.typeDetermination === 'INFERRED') {
                // Clear any resolved types for inferred type variables
                return [variable.name, { ...variable, resolvedType: undefined }];
            } else {
                // Already set the resolved types for fixed type variables
                return [variable.name, { ...variable, resolvedType: variable.type }];
            }
        });
        this.nodeLibraryIDDictionary = nodeLibraryIDDictionary;
    }

    // Helper methods for function-specific resolved node instances
    private getResolvedNodeInstance(nodeId: number): NodeInstance | undefined {
        if (this.currentFunctionContext) {
            return this.functionResolvedNodeInstances[this.currentFunctionContext]?.[nodeId];
        }
        return this.resolvedNodeInstances[nodeId];
    }

    private setResolvedNodeInstance(nodeId: number, nodeInstance: NodeInstance): void {
        if (this.currentFunctionContext) {
            if (!this.functionResolvedNodeInstances[this.currentFunctionContext]) {
                this.functionResolvedNodeInstances[this.currentFunctionContext] = {};
            }
            this.functionResolvedNodeInstances[this.currentFunctionContext][nodeId] = nodeInstance;
        } else {
            this.resolvedNodeInstances[nodeId] = nodeInstance;
        }
    }

    private static getPortTypeFromPortAndGenerics(
        port: NodeInstanceDataPort,
        genericIdentifierTypePairs: { [key: string]: Generic },
    ): NodeInstanceDataPort {
        const portDataType = port.type;
        let resolvedDataType: DataType = resolveGenericsInType(portDataType, genericIdentifierTypePairs);

        return {
            name: port.name,
            type: resolvedDataType,
            optional: port.optional,
        };
    }

    /**
     * Applies a user-defined customType to a port's resolved type. Preserves the
     * optional wrapper if the port was declared optional — the user is picking
     * the inner shape, not changing whether the port is optional.
     */
    private static applyUserDefinedTypeToPort(port: NodeInstanceDataPort, userDefinedType: DataType): NodeInstanceDataPort {
        const type = isOptionalType(port.type) ? createOptional(userDefinedType) : userDefinedType;
        return {
            name: port.name,
            type,
            optional: port.optional,
        };
    }

    private static recurseThroughPortTypeForGenerics(
        portType: DataType,
        valueType: DataType | undefined,
        genericIdentifierTypePairs: { [key: string]: Generic },
    ) {
        switch (portType.kind) {
            case 'string':
            case 'number':
            case 'boolean':
            case 'stringLiteral':
            case 'numberLiteral':
            case 'null':
            case 'secret':
                break;
            case 'union': {
                let valueTypeOptions: DataType[];
                if (valueType) {
                    if (valueType.kind === 'union') {
                        valueTypeOptions = valueType.subTypes;
                    } else {
                        valueTypeOptions = [valueType];
                    }
                } else {
                    valueTypeOptions = [];
                }

                for (const portSubType of portType.subTypes) {
                    if (portSubType.kind === 'generic') {
                        // find all the non-shared subtypes from the valueTypeOptions to pass for the generics
                        const nonSharedConnectionSubTypes = valueTypeOptions.filter(
                            valueTypeOption =>
                                !portType.subTypes
                                    .filter(portSubType => portSubType.kind !== 'generic')
                                    .some(portSubType => typeAMatchesTypeB(valueTypeOption, portSubType)),
                        );
                        // Construct a new datatype from the non shared connection subtypes
                        let nonSharedType: DataType | undefined = undefined;
                        if (nonSharedConnectionSubTypes.length <= 1) {
                            nonSharedType = nonSharedConnectionSubTypes[0];
                        } else if (arrayHasAtLeastOneElement(nonSharedConnectionSubTypes)) {
                            nonSharedType = {
                                kind: 'union',
                                subTypes: nonSharedConnectionSubTypes,
                            };
                        }
                        GraphTypeResolver.recurseThroughPortTypeForGenerics(portSubType, nonSharedType, genericIdentifierTypePairs);
                    } else {
                        // If the port type subtype matches one of the connection subtypes
                        const matchingConnectionSubTypes = valueTypeOptions.filter(connectionSubType =>
                            typeAMatchesTypeB(portSubType, connectionSubType),
                        );
                        if (matchingConnectionSubTypes.length === 0) {
                            // No matches found so just recurse through the port type subtype without a connection type
                            GraphTypeResolver.recurseThroughPortTypeForGenerics(portSubType, undefined, genericIdentifierTypePairs);
                        } else {
                            // Go through all the matching options to make sure we get all chances to match generics
                            for (const matchingConnectionSubType of matchingConnectionSubTypes) {
                                GraphTypeResolver.recurseThroughPortTypeForGenerics(
                                    portSubType,
                                    matchingConnectionSubType,
                                    genericIdentifierTypePairs,
                                );
                            }
                        }
                    }
                }
                break;
            }
            case 'object':
                if (valueType && valueType.kind === 'object') {
                    Object.entries(portType.properties).forEach(([propertyName, propertyType]) => {
                        const valueTypeProperty = valueType.properties[propertyName];
                        GraphTypeResolver.recurseThroughPortTypeForGenerics(propertyType, valueTypeProperty, genericIdentifierTypePairs);
                    });
                } else {
                    Object.values(portType.properties).forEach(propertyType =>
                        GraphTypeResolver.recurseThroughPortTypeForGenerics(propertyType, undefined, genericIdentifierTypePairs),
                    );
                }
                break;
            case 'array':
                if (valueType && valueType.kind === 'array') {
                    GraphTypeResolver.recurseThroughPortTypeForGenerics(portType.subType, valueType.subType, genericIdentifierTypePairs);
                } else {
                    GraphTypeResolver.recurseThroughPortTypeForGenerics(portType.subType, undefined, genericIdentifierTypePairs);
                }
                break;
            case 'generic':
                const preExistingGeneric: Generic | undefined = genericIdentifierTypePairs[portType.identifier];

                const valueTypeWithUnwrappedGenerics = valueType ? unwrapAllResolvedGenerics(valueType) : undefined;

                debugLog(`Resolving generic '${portType.identifier}'`, {
                    preExisting: preExistingGeneric?.type ? getStringRepresentationFromType(preExistingGeneric.type) : 'none',
                    valueType: valueTypeWithUnwrappedGenerics
                        ? getStringRepresentationFromType(valueTypeWithUnwrappedGenerics)
                        : 'undefined',
                    rawValueType: valueTypeWithUnwrappedGenerics,
                });

                // If generic pre-exists
                if (preExistingGeneric !== undefined) {
                    // If there is a connection input result
                    // if (connectionInputNodeTypeInformation !== undefined && valueType !== undefined) {
                    // If the pre-existing generic has a type already
                    if (preExistingGeneric.type === undefined) {
                        debugLog(`Setting generic '${portType.identifier}' to:`, valueTypeWithUnwrappedGenerics);
                        genericIdentifierTypePairs[portType.identifier].type = valueTypeWithUnwrappedGenerics;
                    } else {
                        debugLog(`Generic '${portType.identifier}' already has type, keeping existing`);
                    }
                } else {
                    debugLog(`Creating new generic '${portType.identifier}' with type:`, valueTypeWithUnwrappedGenerics);
                    genericIdentifierTypePairs[portType.identifier] = {
                        identifier: portType.identifier,
                        type: valueTypeWithUnwrappedGenerics,
                    };
                }
                break;
            case 'optional':
                if (valueType && valueType.kind === 'optional') {
                    GraphTypeResolver.recurseThroughPortTypeForGenerics(
                        portType.innerType,
                        valueType.innerType,
                        genericIdentifierTypePairs,
                    );
                } else {
                    // A concrete type connecting to Optional<T> — match against inner type
                    GraphTypeResolver.recurseThroughPortTypeForGenerics(portType.innerType, valueType, genericIdentifierTypePairs);
                }
                break;
            case 'function':
                // Recurse through function input and output types
                if (valueType && valueType.kind === 'function') {
                    // Match input types
                    Object.entries(portType.inputTypes).forEach(([portName, expectedInputType]) => {
                        const actualInputType = valueType.inputTypes[portName];
                        if (actualInputType) {
                            GraphTypeResolver.recurseThroughPortTypeForGenerics(
                                expectedInputType,
                                actualInputType,
                                genericIdentifierTypePairs,
                            );
                        }
                    });

                    // Match output types
                    Object.entries(portType.outputTypes).forEach(([portName, expectedOutputType]) => {
                        const actualOutputType = valueType.outputTypes[portName];
                        if (actualOutputType) {
                            GraphTypeResolver.recurseThroughPortTypeForGenerics(
                                expectedOutputType,
                                actualOutputType,
                                genericIdentifierTypePairs,
                            );
                        }
                    });
                } else {
                    // No connection type, recurse through function types without matching
                    Object.values(portType.inputTypes).forEach(inputType =>
                        GraphTypeResolver.recurseThroughPortTypeForGenerics(inputType, undefined, genericIdentifierTypePairs),
                    );
                    Object.values(portType.outputTypes).forEach(outputType =>
                        GraphTypeResolver.recurseThroughPortTypeForGenerics(outputType, undefined, genericIdentifierTypePairs),
                    );
                }
                break;
        }
    }

    private matchFunctionSignatureAgainstFunctionType(
        functionType: FunctionDataType,
        functionDef: FunctionDefinition,
        genericIdentifierTypePairs: { [key: string]: Generic },
    ) {
        debugLog(`=== Matching function '${functionDef.name}' against expected signature ===`);
        debugLog(
            `Expected function inputTypes:`,
            Object.fromEntries(Object.entries(functionType.inputTypes).map(([k, v]) => [k, getStringRepresentationFromType(v)])),
        );
        debugLog(
            `Actual function inputPortDefinitions:`,
            functionDef.inputPortDefinitions.map(p => ({
                name: p.name,
                type: getStringRepresentationFromType(p.type),
                rawType: p.type,
            })),
        );

        // Match input types - the expected type (with generics) against actual function definition
        Object.entries(functionType.inputTypes).forEach(([portName, expectedInputType]) => {
            const actualInputPort = functionDef.inputPortDefinitions.find(p => p.name === portName);
            debugLog(`Matching input port '${portName}':`, {
                expected: getStringRepresentationFromType(expectedInputType),
                actual: actualInputPort ? getStringRepresentationFromType(actualInputPort.type) : 'NOT FOUND',
                rawExpected: expectedInputType,
                rawActual: actualInputPort?.type,
            });
            if (actualInputPort) {
                GraphTypeResolver.recurseThroughPortTypeForGenerics(
                    expectedInputType, // Expected type (may contain generics)
                    actualInputPort.type, // Actual type from function definition (concrete type)
                    genericIdentifierTypePairs,
                );
            }
        });

        // Match output types - the expected type (with generics) against actual function definition
        Object.entries(functionType.outputTypes).forEach(([portName, expectedOutputType]) => {
            const actualOutputPort = functionDef.outputPortDefinitions.find(p => p.name === portName);
            if (actualOutputPort) {
                GraphTypeResolver.recurseThroughPortTypeForGenerics(
                    expectedOutputType, // Expected type (may contain generics)
                    actualOutputPort.type, // Actual type from function definition (concrete type)
                    genericIdentifierTypePairs,
                );
            }
        });
    }

    private resolveInPortType(
        inPort: NodeInstanceDataPort,
        inputConnectionsForThisNodeInstance: DataConnection[],
        genericIdentifierTypePairs: { [key: string]: Generic },
        nodeInstance: NodeInstance,
    ) {
        // We can optimize and only check for the port name because we have narrowed the connections down to ones from this node already.
        const inputConnection: DataConnection | undefined = inputConnectionsForThisNodeInstance.find(
            connection => connection.receivingPortName === inPort.name,
        );

        let connectionInputNodeOutPortTypeInformation: NodeInstanceDataPort | undefined = undefined;
        if (inputConnection) {
            // Find the connection node instance from the current context's nodeInstances
            const connectionNodeInstance = this.nodeInstances.find(node => node.id === inputConnection.sendingNodeInstanceId);
            if (!connectionNodeInstance) {
                throw new Error(`Could not find a node instance with the id: ${inputConnection.sendingNodeInstanceId} in graph.`);
            }

            const connectionNode: Node | undefined = this.nodeLibraryIDDictionary[connectionNodeInstance.nodeId];
            if (!connectionNode) {
                throw new Error(`Could not find a node with the id: ${connectionNodeInstance.nodeId} in node library.`);
            }

            // No subgraph handling - check normal external connections only
            {
                const fromConnectionResolvedNodeInstanceTypes =
                    this.getResolvedNodeInstance(connectionNodeInstance.id) ?? this.getNodeInstanceTypeInformation(connectionNodeInstance);
                connectionInputNodeOutPortTypeInformation = fromConnectionResolvedNodeInstanceTypes.outPorts.find(
                    outPort => outPort.name === inputConnection.sendingPortName,
                );
            }
        }
        // When there's no incoming connection, a user-defined customType acts as
        // the effective value type for this port. This lets generics referenced
        // elsewhere in the node pick up the user's choice via identifier.
        const userDefinedType = !inputConnection ? GraphTypeResolver.getUserDefinedCustomType(nodeInstance, inPort.name) : undefined;
        const valueType = connectionInputNodeOutPortTypeInformation?.type ?? userDefinedType;
        if (inPort.type !== undefined) {
            GraphTypeResolver.recurseThroughPortTypeForGenerics(inPort.type, valueType, genericIdentifierTypePairs);
        }
    }

    private static getUserDefinedCustomType(nodeInstance: NodeInstance, portName: string): DataType | undefined {
        const userDefined = nodeInstance.portUserDefinedTypes?.[portName];
        if (userDefined && userDefined.kind === 'customType') {
            return userDefined.type;
        }
        return undefined;
    }

    private setResolvedVariableType = (variableName: string, type?: DataType) => {
        const variable = this.variables[variableName];
        if (variable && variable.typeDetermination === 'INFERRED' && type) {
            this.variables[variable.name].resolvedType = type;
        }
    };

    private getNodeInstanceTypeInformation(nodeInstance: NodeInstance): NodeInstance {
        const node: Node | undefined = this.nodeLibraryIDDictionary[nodeInstance.nodeId];
        if (!node) {
            throw new Error(
                `Could not find node in node library with id: ${nodeInstance.nodeId}. Found on node instance with id: ${nodeInstance.id}`,
            );
        }

        const inputConnectionsForThisNodeInstance: DataConnection[] = this.connections.filter(
            connection => connection.receivingNodeInstanceId === nodeInstance.id,
        );

        const genericIdentifierTypePairs: { [key: string]: Generic } = {};

        // FIRST: Process function-type ports with function reference values to establish generics
        // We process these first as they can be seen as more "inherent" and internal to the node,
        // so that it is correct for them to take precedence over incoming connections.
        for (const inPort of nodeInstance.inPorts) {
            if (inPort.type?.kind === 'function') {
                const portValue = nodeInstance.portValues[inPort.name];
                if (isFunctionReferenceValue(portValue)) {
                    const referencedFunc = this.functions.find(f => f.id === portValue.functionId);
                    if (referencedFunc) {
                        this.matchFunctionSignatureAgainstFunctionType(inPort.type, referencedFunc, genericIdentifierTypePairs);
                    }
                }
            }
        }

        // SECOND: Process all ports with connections to resolve remaining generics
        for (const inPort of nodeInstance.inPorts) {
            this.resolveInPortType(inPort, inputConnectionsForThisNodeInstance, genericIdentifierTypePairs, nodeInstance);
        }

        // Output ports with user-defined customTypes feed generic identifiers too,
        // so references to the same generic on input ports pick up the user's choice.
        for (const outPort of nodeInstance.outPorts) {
            const userDefinedOutType = GraphTypeResolver.getUserDefinedCustomType(nodeInstance, outPort.name);
            if (userDefinedOutType !== undefined && outPort.type !== undefined) {
                GraphTypeResolver.recurseThroughPortTypeForGenerics(outPort.type, userDefinedOutType, genericIdentifierTypePairs);
            }
        }

        const portHasIncomingConnection = (portName: string): boolean =>
            inputConnectionsForThisNodeInstance.some(connection => connection.receivingPortName === portName);

        const resolvedInPorts: NodeInstanceDataPort[] = nodeInstance.inPorts.map(inPort => {
            const resolved = GraphTypeResolver.getPortTypeFromPortAndGenerics(inPort, genericIdentifierTypePairs);
            if (portHasIncomingConnection(inPort.name)) {
                return resolved;
            }
            const userDefinedType = GraphTypeResolver.getUserDefinedCustomType(nodeInstance, inPort.name);
            if (userDefinedType === undefined) {
                return resolved;
            }
            return GraphTypeResolver.applyUserDefinedTypeToPort(resolved, userDefinedType);
        });

        // Initial graph walking and generics establishment phase
        let updatedNodeInstance: NodeInstance;
        if (node.computeShape) {
            // Get current function context for function-related nodes
            const currentFunctionDefinition = this.currentFunctionContext
                ? this.functions.find(f => f.id === this.currentFunctionContext)
                : undefined;
            const referencedFunctionId = isFunctionNodeInstance(nodeInstance) ? nodeInstance.functionId : undefined;

            const newIO = node.computeShape({
                preResolvedInputPorts: resolvedInPorts,
                preResolvedOutputPorts: nodeInstance.outPorts,
                nodeInPorts: node.inPorts,
                nodeOutPorts: node.outPorts,
                portValues: nodeInstance.portValues,
                state: nodeInstance.state,
                setVariableType: this.setResolvedVariableType,
                selectedVariable: nodeInstance.selectedVariable !== undefined ? this.variables[nodeInstance.selectedVariable] : undefined,
                // Pass function context for function-related nodes
                referencedFunctionId: referencedFunctionId,
                containingFunctionDefinition: currentFunctionDefinition,
                allFunctions: this.functions,
            });
            const newInPorts = newIO.inPorts.map(inPort =>
                GraphTypeResolver.getPortTypeFromPortAndGenerics(inPort, genericIdentifierTypePairs),
            );
            updatedNodeInstance = {
                ...nodeInstance,
                inPorts: newInPorts,
                outPorts: newIO.outPorts,
            };
        } else {
            updatedNodeInstance = {
                ...nodeInstance,
                inPorts: resolvedInPorts,
                outPorts: nodeInstance.outPorts,
            };
        }

        const inPortTypes: NodeInstanceDataPort[] = updatedNodeInstance.inPorts.map(inPort => {
            const resolved = GraphTypeResolver.getPortTypeFromPortAndGenerics(inPort, genericIdentifierTypePairs);
            if (portHasIncomingConnection(inPort.name)) {
                return resolved;
            }
            const userDefinedType = GraphTypeResolver.getUserDefinedCustomType(nodeInstance, inPort.name);
            if (userDefinedType === undefined) {
                return resolved;
            }
            return GraphTypeResolver.applyUserDefinedTypeToPort(resolved, userDefinedType);
        });
        const outPortTypes: NodeInstanceDataPort[] = updatedNodeInstance.outPorts.map(outPort => {
            const resolved = GraphTypeResolver.getPortTypeFromPortAndGenerics(outPort, genericIdentifierTypePairs);
            const userDefinedType = GraphTypeResolver.getUserDefinedCustomType(nodeInstance, outPort.name);
            if (userDefinedType === undefined) {
                return resolved;
            }
            return GraphTypeResolver.applyUserDefinedTypeToPort(resolved, userDefinedType);
        });

        const resolvedNodeInstance: NodeInstance = {
            ...updatedNodeInstance,
            inPorts: inPortTypes,
            outPorts: outPortTypes,
        };
        this.setResolvedNodeInstance(nodeInstance.id, resolvedNodeInstance);

        return resolvedNodeInstance;
    }

    private getNodeInstanceTypeInformationForAllRootsInNodesList(nodeInstances: NodeInstance[]) {
        const connectionsSendingNodeInstanceIDDictionary = dictionaryFromNumberTuple(
            this.connections, // No internal connections exist
            (connection: DataConnection) => [connection.sendingNodeInstanceId, connection],
        );
        const rootNodeInstances: NodeInstance[] = nodeInstances.filter(nodeInstance => {
            const connection = connectionsSendingNodeInstanceIDDictionary[nodeInstance.id];
            return connection === undefined;
        });

        for (const nodeInstance of rootNodeInstances) {
            this.getNodeInstanceTypeInformation(nodeInstance);
        }

        // Roots-first traversal walks up via external inPort connections, so any
        // node only reachable via inherent edges (e.g. a trigger feeding a
        // RespondToApi via the inherent in-port) is never visited. Post-Phase 3
        // `outPorts` are derived from `computeShape`, so an unvisited node ends
        // up with empty `outPorts` and `cleanupInvalidConnections` drops its
        // outgoing connections. Resolve any remaining nodes here.
        for (const nodeInstance of nodeInstances) {
            if (this.getResolvedNodeInstance(nodeInstance.id) === undefined) {
                this.getNodeInstanceTypeInformation(nodeInstance);
            }
        }
    }

    private resolveAllFunctions(): void {
        // Resolve functions in dependency order to handle function-referencing functions
        const functionResolutionOrder = this.determineFunctionResolutionOrder();

        for (const functionId of functionResolutionOrder) {
            const functionDef = this.functions.find(f => f.id === functionId);
            if (functionDef) {
                this.resolveSingleFunction(functionDef);
            }
        }
    }

    private determineFunctionResolutionOrder(): string[] {
        // FIXME to be replaced with the proper order based on dependencies
        // Functions should be independent boundaries - no interdependencies <- this is wrong
        // Resolve in definition order
        return this.functions.map(f => f.id);
    }

    private resolveSingleFunction(functionDef: FunctionDefinition): void {
        // Store original context
        const originalNodeInstances = this.nodeInstances;
        const originalConnections = this.connections;
        const originalFunctionContext = this.currentFunctionContext;

        // Switch to function's internal context
        this.currentFunctionContext = functionDef.id;
        this.nodeInstances = functionDef.nodeInstances;
        this.connections = functionDef.connections;

        // Resolve function's internal graph
        this.getNodeInstanceTypeInformationForAllRootsInNodesList(this.nodeInstances);

        // Store the resolved internal node instances for the function
        // but NOT the resolved interface types (those belong on function node instances)
        this.resolvedFunctionGraphs[functionDef.id] = {
            nodeInstances: this.nodeInstances.map(node => this.getResolvedNodeInstance(node.id) ?? node),
        };

        // Restore original context
        this.currentFunctionContext = originalFunctionContext;
        this.nodeInstances = originalNodeInstances;
        this.connections = originalConnections;
    }

    getResolvedGraph(): DataGlueGraph {
        // Phase 1: Resolve all functions first
        this.resolveAllFunctions();

        // Phase 2: Resolve root graph with function context
        this.getNodeInstanceTypeInformationForAllRootsInNodesList(this.nodeInstances);

        // Build resolved functions with their resolved internal graphs
        const resolvedFunctions = this.functions.map(func => ({
            ...func,
            nodeInstances: this.resolvedFunctionGraphs[func.id]?.nodeInstances || func.nodeInstances,
        }));

        return {
            // Grabbing the resolved node instances in the order of the input to make sure the order stays stable
            nodeInstances: this.nodeInstances.map(nodeInstance => this.getResolvedNodeInstance(nodeInstance.id) ?? nodeInstance),
            connections: this.connections,
            variables: Object.values(this.variables),
            functions: resolvedFunctions.length > 0 ? resolvedFunctions : this.functions,
        };
    }
}
